/**
 * TaskFilters Component
 *
 * Provider, type, and status filters. Priority is a sortable column, not a filter.
 */

import { Box, Group, Switch, Text } from '@mantine/core';
import { IconFilter } from '@tabler/icons-react';
import { FilterMultiSelect } from '../../../components/FilterMultiSelect';
import { SearchInput } from '../../../components/SearchInput';
import type { TaskFilters as TaskFiltersType, TaskInboxStatus } from '../types';
import { TASK_STATUS_FILTER_OPTIONS } from '../types';
import { TASK_TYPE_OPTIONS } from '../data';
import type { TeamMember } from '../hooks/useTeamMembers';

const PROVIDER_OPTIONS_ABOVE_DIVIDER = ['mine', 'team'];

interface TaskFiltersProps {
  filters: TaskFiltersType;
  onFiltersChange: (filters: TaskFiltersType) => void;
  overdueCount: number;
  teamMembers: TeamMember[];
}

export function TaskFiltersPanel({
  filters,
  onFiltersChange,
  overdueCount,
  teamMembers,
}: TaskFiltersProps): JSX.Element {
  const providerOptions = [
    { value: 'mine', label: 'My Tasks' },
    ...(teamMembers.length > 0 ? [{ value: 'team', label: 'My Team' }] : []),
    ...teamMembers.map((m) => ({ value: m.reference, label: m.name })),
  ];

  const taskTypeOptions = TASK_TYPE_OPTIONS.map((t) => ({ value: t.value, label: t.label }));

  return (
    <Group gap="md" wrap="wrap" align="center" flex="1">
      <SearchInput
        placeholder="Search tasks..."
        value={filters.search}
        onChange={(e) => onFiltersChange({ ...filters, search: e.currentTarget.value })}
        w={280}
      />

      <FilterMultiSelect
        allLabel="All Providers"
        leftSection={<IconFilter size={14} color="var(--mantine-color-gray-5)" />}
        data={providerOptions}
        value={filters.provider}
        onChange={(value) => onFiltersChange({ ...filters, provider: value })}
        selectedLabel="Selected"
        optionsAboveDivider={PROVIDER_OPTIONS_ABOVE_DIVIDER}
        w={280}
        style={{ flexShrink: 0, minWidth: 260 }}
      />

      <FilterMultiSelect
        allLabel="All Task Types"
        leftSection={<IconFilter size={14} color="var(--mantine-color-gray-5)" />}
        data={taskTypeOptions}
        value={filters.taskType}
        onChange={(value) => onFiltersChange({ ...filters, taskType: value })}
        w={200}
        style={{ flexShrink: 0 }}
      />

      <FilterMultiSelect
        allLabel="All Statuses"
        leftSection={<IconFilter size={14} color="var(--mantine-color-gray-5)" />}
        data={TASK_STATUS_FILTER_OPTIONS}
        value={filters.status}
        onChange={(value) => onFiltersChange({ ...filters, status: value as TaskInboxStatus[] })}
        w={180}
        style={{ flexShrink: 0 }}
      />

      <Box style={{ flexShrink: 0 }}>
        <Switch
          label={
            <Group gap={4}>
              <Text size="sm">Overdue{overdueCount > 0 ? ` (${overdueCount})` : ''}</Text>
            </Group>
          }
          checked={filters.showOverdueOnly}
          onChange={() => onFiltersChange({
            ...filters,
            showOverdueOnly: !filters.showOverdueOnly,
          })}
          color="gold"
        />
      </Box>
    </Group>
  );
}
